<template>
  <v-container fluid class="approval-page">
    <div class="page-header">
      <div>
        <div class="eyebrow">Compras</div>
        <h1>Aprobación de reposiciones</h1>
        <p>Revisá solicitudes internas y generá la orden de compra formal al aprobar.</p>
      </div>
      <v-btn outlined color="primary" :to="{ name: 'internal-orders-list' }">
        <v-icon left>mdi-format-list-bulleted</v-icon>
        Ver historial
      </v-btn>
    </div>

    <v-row class="metrics-row">
      <v-col cols="12" sm="6" lg="3">
        <div class="metric-card">
          <v-icon color="primary">mdi-clipboard-clock-outline</v-icon>
          <div>
            <span>Solicitudes pendientes</span>
            <strong>{{ summary.totalPending }}</strong>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <div class="metric-card">
          <v-icon color="primary">mdi-package-variant</v-icon>
          <div>
            <span>Ítems a evaluar</span>
            <strong>{{ summary.totalPendingItems }}</strong>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <div class="metric-card">
          <v-icon color="primary">mdi-counter</v-icon>
          <div>
            <span>Unidades solicitadas</span>
            <strong>{{ formatQuantity(summary.totalQuantity) }}</strong>
          </div>
        </div>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <div class="metric-card">
          <v-icon color="primary">mdi-cash-multiple</v-icon>
          <div>
            <span>Costo estimado</span>
            <strong>{{ formatMoney(summary.totalEstimatedCost) }}</strong>
            <small v-if="summary.missingCostItems">{{ summary.missingCostItems }} sin costo</small>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card flat class="toolbar-panel">
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              label="Filtrar proveedor, solicitante o artículo"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="convertData.warehouseId"
              :items="warehouses"
              item-text="name"
              item-value="id"
              outlined
              dense
              hide-details
              label="Depósito destino por defecto"
            />
          </v-col>
          <v-col cols="12" md="3" class="text-md-right">
            <v-btn color="primary" :loading="loading" @click="loadDashboard">
              <v-icon left>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" size="56" />
      <span>Cargando solicitudes...</span>
    </div>

    <v-alert v-else-if="filteredSuppliers.length === 0" type="success" text class="mt-4">
      No hay solicitudes pendientes para aprobar.
    </v-alert>

    <v-expansion-panels v-else v-model="expandedPanels" multiple class="supplier-panels">
      <v-expansion-panel v-for="supplier in filteredSuppliers" :key="supplier.supplierId" class="supplier-panel">
        <v-expansion-panel-header>
          <div class="supplier-header">
            <div>
              <div class="supplier-name">{{ supplier.supplierName }}</div>
              <div class="supplier-meta">
                {{ supplier.supplierCode }} · {{ supplier.pendingOrdersCount }} solicitudes ·
                {{ supplier.pendingItemsCount }} ítems · {{ formatQuantity(supplier.totalQuantity) }} unidades
              </div>
            </div>
            <div class="supplier-totals">
              <v-chip small color="primary" text-color="white">{{ formatMoney(supplier.totalEstimatedCost) }}</v-chip>
              <v-chip v-if="supplier.missingCostItems" small outlined color="warning">
                {{ supplier.missingCostItems }} sin costo
              </v-chip>
            </div>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <div class="order-card" v-for="order in supplier.orders" :key="order.id">
            <div class="order-head">
              <div>
                <div class="order-title">Solicitud del {{ formatDate(order.orderDate) }}</div>
                <div class="order-meta">
                  <v-icon x-small>mdi-account-outline</v-icon>
                  {{ order.createdBy }}
                  <span v-if="order.notes">· {{ order.notes }}</span>
                </div>
                <div class="order-kpis">
                  <span>{{ order.itemsCount }} ítems</span>
                  <span>{{ formatQuantity(order.totalQuantity) }} unidades</span>
                  <span>{{ formatMoney(order.totalEstimatedCost) }}</span>
                  <span v-if="order.missingCostItems" class="warning-text">{{ order.missingCostItems }} sin costo</span>
                </div>
              </div>
              <div class="order-actions">
                <v-btn small outlined color="primary" @click="viewOrder(order.id)">
                  <v-icon small left>mdi-eye-outline</v-icon>
                  Ver
                </v-btn>
                <v-btn small color="primary" :loading="approvingId === order.id" @click="openApproveDialog(supplier, order)">
                  <v-icon small left>mdi-check-decagram-outline</v-icon>
                  Aprobar y crear OC
                </v-btn>
                <v-btn small outlined color="error" :loading="cancellingId === order.id" @click="confirmCancel(order)">
                  <v-icon small left>mdi-close-circle-outline</v-icon>
                  Rechazar
                </v-btn>
              </div>
            </div>

            <v-simple-table dense class="items-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Artículo</th>
                  <th class="text-right">Cant.</th>
                  <th class="text-right">Costo ref.</th>
                  <th class="text-right">Subtotal</th>
                  <th>Nota</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in order.items" :key="item.id">
                  <td><span class="code-pill">{{ item.productCode || "-" }}</span></td>
                  <td>
                    <div class="item-name">{{ item.productName || "-" }}</div>
                    <div class="item-meta">{{ getCostSourceLabel(item.costSource) }}</div>
                  </td>
                  <td class="text-right item-quantity">{{ formatQuantity(item.quantity) }}</td>
                  <td class="text-right">
                    <span v-if="hasCost(item)">{{ formatMoney(item.unitCost) }}</span>
                    <span v-else class="missing-cost">Sin costo</span>
                  </td>
                  <td class="text-right">
                    <span v-if="hasCost(item)">{{ formatMoney(item.lineTotal) }}</span>
                    <span v-else>-</span>
                  </td>
                  <td class="item-note">{{ item.notes || "-" }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-dialog v-model="approveDialog" max-width="560" persistent>
      <v-card v-if="selectedOrder">
        <v-card-title class="dialog-title">
          <v-icon left color="white">mdi-check-decagram-outline</v-icon>
          Aprobar solicitud
        </v-card-title>
        <v-card-text class="pt-5">
          <v-alert type="info" text>
            Se va a crear una orden de compra formal para <strong>{{ selectedSupplier?.supplierName }}</strong>.
          </v-alert>
          <v-form ref="approveForm" v-model="approveFormValid">
            <v-select
              v-model="convertData.warehouseId"
              :items="warehouses"
              item-text="name"
              item-value="id"
              label="Depósito destino"
              outlined
              dense
              :rules="[v => !!v || 'Seleccione un depósito']"
            />
            <v-textarea
              v-model="convertData.notes"
              label="Nota para la orden de compra"
              outlined
              dense
              rows="3"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="approvingId === selectedOrder.id" @click="approveDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="approvingId === selectedOrder.id" :disabled="!approveFormValid" @click="approveSelectedOrder">
            Aprobar y crear OC
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="460">
      <v-card v-if="orderToCancel">
        <v-card-title class="error white--text">
          <v-icon left color="white">mdi-close-circle-outline</v-icon>
          Rechazar solicitud
        </v-card-title>
        <v-card-text class="pt-4">
          Esta acción cancela la solicitud interna. No se generará orden de compra.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="Boolean(cancellingId)" @click="cancelDialog = false">Volver</v-btn>
          <v-btn color="error" :loading="cancellingId === orderToCancel.id" @click="cancelSelectedOrder">Rechazar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="4000"
      top
      right
      :class="[`module-toast`, `module-toast--${snackbar.color}`]"
      content-class="white--text font-weight-bold"
    >
      {{ snackbar.message }}
      <template v-slot:action>
        <v-btn text color="white" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { approveOrder, cancelOrder, generateIdempotencyKey, getDashboard } from "@/services/internalOrders";
import { getWarehouses } from "@/services/warehouses";

export default {
  name: "InternalOrdersDashboard",
  data() {
    return {
      loading: true,
      search: "",
      summary: {
        totalPending: 0,
        totalPendingItems: 0,
        pendingBySupplier: 0,
        totalQuantity: 0,
        totalEstimatedCost: 0,
        missingCostItems: 0,
      },
      suppliers: [],
      expandedPanels: [],
      warehouses: [],
      approveDialog: false,
      approveFormValid: false,
      selectedSupplier: null,
      selectedOrder: null,
      approvingId: null,
      convertData: { warehouseId: null, notes: "" },
      cancelDialog: false,
      orderToCancel: null,
      cancellingId: null,
      eventSource: null,
      snackbar: { show: false, message: "", color: "success" },
    };
  },
  computed: {
    filteredSuppliers() {
      const term = String(this.search || "").trim().toLowerCase();
      if (!term) return this.suppliers;
      return this.suppliers
        .map((supplier) => ({
          ...supplier,
          orders: supplier.orders.filter((order) => {
            const haystack = [
              supplier.supplierName,
              supplier.supplierCode,
              order.createdBy,
              order.notes,
              ...(order.items || []).flatMap((item) => [item.productCode, item.productName, item.notes]),
            ].join(" ").toLowerCase();
            return haystack.includes(term);
          }),
        }))
        .filter((supplier) => supplier.orders.length > 0);
    },
  },
  mounted() {
    this.loadDashboard();
    this.loadWarehouses();
    this.setupSSE();
  },
  beforeDestroy() {
    if (this.eventSource) this.eventSource.close();
  },
  methods: {
    async loadDashboard() {
      this.loading = true;
      try {
        const response = await getDashboard();
        if (response.ok) {
          this.summary = response.data.summary;
          this.suppliers = response.data.suppliers || [];
          this.expandedPanels = this.suppliers.map((_, i) => i);
        } else {
          this.showError(response.error || "Error al cargar solicitudes");
        }
      } catch (error) {
        console.error("Error loading dashboard:", error);
        this.showError("Error al cargar solicitudes");
      } finally {
        this.loading = false;
      }
    },
    async loadWarehouses() {
      try {
        const response = await getWarehouses();
        if (response.ok) {
          this.warehouses = response.data || [];
          this.convertData.warehouseId = this.warehouses[0]?.id || null;
        }
      } catch (error) {
        console.error("Error loading warehouses:", error);
      }
    },
    setupSSE() {
      const baseUrl = process.env.VUE_APP_API_URL || `${window.location.origin}/apiv3`;
      this.eventSource = new EventSource(`${baseUrl}/realtime/stream`);
      ["internalOrder:created", "internalOrder:approved", "internalOrder:converted", "internalOrder:cancelled"].forEach((eventName) => {
        this.eventSource.addEventListener(eventName, () => this.loadDashboard());
      });
      this.eventSource.onerror = (error) => console.error("SSE error:", error);
    },
    openApproveDialog(supplier, order) {
      this.selectedSupplier = supplier;
      this.selectedOrder = order;
      this.convertData = {
        warehouseId: this.convertData.warehouseId || this.warehouses[0]?.id || null,
        notes: order.notes || "",
      };
      this.approveDialog = true;
    },
    async approveSelectedOrder() {
      if (!this.$refs.approveForm.validate()) return;
      this.approvingId = this.selectedOrder.id;
      try {
        const response = await approveOrder(
          this.selectedOrder.id,
          this.convertData,
          generateIdempotencyKey("approve")
        );
        if (response.ok) {
          this.showSuccess(response.data.message || "Solicitud aprobada");
          this.approveDialog = false;
          this.loadDashboard();
        } else {
          this.showError(response.error || "Error al aprobar");
        }
      } catch (error) {
        console.error("Error approving order:", error);
        this.showError(error.response?.data?.error || "Error al aprobar");
      } finally {
        this.approvingId = null;
      }
    },
    confirmCancel(order) {
      this.orderToCancel = order;
      this.cancelDialog = true;
    },
    async cancelSelectedOrder() {
      this.cancellingId = this.orderToCancel.id;
      try {
        const response = await cancelOrder(this.orderToCancel.id, generateIdempotencyKey("reject"));
        if (response.ok) {
          this.showSuccess("Solicitud rechazada");
          this.cancelDialog = false;
          this.loadDashboard();
        } else {
          this.showError(response.error || "Error al rechazar");
        }
      } catch (error) {
        console.error("Error rejecting order:", error);
        this.showError(error.response?.data?.error || "Error al rechazar");
      } finally {
        this.cancellingId = null;
      }
    },
    viewOrder(orderId) {
      this.$router.push({ name: "internal-order-detail", params: { id: orderId } });
    },
    formatDate(dateStr) {
      return dateStr ? new Date(dateStr).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
    },
    formatQuantity(value) {
      return new Intl.NumberFormat("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(value || 0));
    },
    formatMoney(value) {
      return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(Number(value || 0));
    },
    hasCost(item) {
      return item.unitCost !== null && item.unitCost !== undefined && Number(item.unitCost) > 0;
    },
    getCostSourceLabel(source) {
      if (source === "supplier") return "Costo de proveedor";
      if (source === "standard") return "Costo estándar";
      return "Costo pendiente";
    },
    showSuccess(message) {
      this.snackbar = { show: true, message, color: "success" };
    },
    showError(message) {
      this.snackbar = { show: true, message, color: "error" };
    },
  },
};
</script>

<style scoped>
.approval-page {
  --module-primary: var(--nav-link-active-color, #223b6a);
  --module-action: var(--accent-primary, #3b82f6);
  --module-line: var(--border-color, rgba(26, 35, 51, 0.1));
  --module-muted: var(--text-tertiary, #64748b);
  background: transparent;
  min-height: calc(100vh - 64px);
  padding-top: 12px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.page-header h1 {
  margin: 0;
  color: var(--module-primary);
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
}

.page-header p {
  margin: 4px 0 0;
  color: var(--module-muted);
  font-size: 14px;
}

.eyebrow {
  color: var(--module-action);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.metric-card,
.toolbar-panel,
.supplier-panel {
  border: 1px solid var(--module-line);
  border-radius: 8px;
  background: var(--bg-secondary, #fff) !important;
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 72px;
  padding: 12px 14px;
}

.metric-card span {
  display: block;
  color: var(--module-muted);
  font-size: 12px;
}

.metric-card strong {
  display: block;
  color: var(--text-primary, #1a2333);
  font-size: 20px;
  line-height: 1.2;
}

.metric-card small {
  display: block;
  color: #a16207;
  font-size: 11px;
  font-weight: 700;
  margin-top: 2px;
}

.toolbar-panel {
  margin: 8px 0 14px;
}

.loading-state {
  display: grid;
  place-items: center;
  gap: 12px;
  min-height: 220px;
  color: var(--module-muted);
}

.supplier-panels .v-expansion-panel {
  margin-bottom: 12px;
}

.supplier-header,
.order-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.supplier-name,
.order-title {
  color: var(--text-primary, #1a2333);
  font-weight: 700;
}

.supplier-name {
  font-size: 14px;
}

.supplier-meta,
.order-meta {
  color: var(--module-muted);
  font-size: 12px;
}

.supplier-totals {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.order-card {
  border: 1px solid var(--module-line);
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.order-head {
  padding: 12px 14px;
  background: var(--surface-muted, rgba(241, 245, 249, 0.9));
  border-bottom: 1px solid var(--module-line);
  align-items: flex-start;
}

.order-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.order-kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.order-kpis span {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border: 1px solid var(--module-line);
  border-radius: 999px;
  background: var(--bg-secondary, #fff);
  color: var(--text-secondary, #475569);
  font-size: 11px;
  font-weight: 700;
}

.order-kpis .warning-text {
  border-color: rgba(161, 98, 7, 0.28);
  background: rgba(245, 158, 11, 0.12);
  color: #a16207;
}

.items-table th {
  color: var(--text-secondary, #475569) !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.items-table td,
.toolbar-panel >>> .v-label,
.toolbar-panel >>> input,
.toolbar-panel >>> .v-select__selection {
  font-size: 13px;
}

.item-name {
  color: var(--text-primary, #1a2333);
  font-weight: 600;
}

.item-meta,
.item-note {
  color: var(--module-muted);
  font-size: 12px;
}

.item-quantity {
  color: var(--text-primary, #1a2333);
  font-weight: 700;
}

.missing-cost {
  color: #a16207;
  font-size: 12px;
  font-weight: 700;
}

.code-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border: 1px solid rgba(59, 130, 246, 0.28);
  border-radius: 6px;
  color: var(--module-primary);
  background: var(--accent-primary-light, rgba(59, 130, 246, 0.14));
  font-size: 11px;
  font-weight: 700;
}

.dialog-title {
  background: var(--module-primary);
  color: #fff;
}

.module-toast .v-snack__wrapper {
  border-radius: 8px !important;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.24) !important;
}

.module-toast--success .v-snack__wrapper {
  background: #166534 !important;
}

.module-toast--error .v-snack__wrapper {
  background: #991b1b !important;
}

@media (max-width: 780px) {
  .page-header,
  .supplier-header,
  .order-head {
    flex-direction: column;
    align-items: stretch;
  }

  .order-actions > * {
    width: 100%;
  }

  .supplier-totals {
    justify-content: flex-start;
  }
}
</style>
